import { useMemo } from 'react';

import {
  array,
  option,
  pipe,
  record,
  remote,
  remoteOption,
  struct,
  task,
} from '@code-expert/prelude';
import type { PublicationSet } from '@code-expert/pubsub-common';
import type { TaskId } from '/imports/domain';
import type {
  QuestionId,
  QuestionVersionId,
  QuizId,
  QuizSnapshotId,
} from '/imports/modules/Quizzes/domain';
import type {
  QuestionMutableProps,
  QuizMutableProps,
  QuizSnapshotInsert,
} from '/imports/modules/Quizzes/manage/shared/domain';
import type {
  Question,
  QuestionItem,
  Quiz,
  QuizSnapshot,
} from '/imports/modules/Quizzes/shared/presentation/domain';
import type { QuizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import { quizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import { invokeProcedure, useCachedQuery } from '/imports/ui/hooks/useProcedure';
import { usePublication } from '/imports/ui/hooks/usePublication';

export interface ManageQuizApi extends QuizApi {
  useQuizVersionId: (
    taskId: TaskId,
    snapshotDate: option.Option<Date>,
  ) => remoteOption.RemoteOption<QuizId>;

  useQuizVersionSubscription: (
    taskId: TaskId,
    snapshotDate: option.Option<Date>,
  ) => remoteOption.RemoteOption<Quiz>;

  useQuizSnapshotSubscription: (
    taskId: TaskId,
    snapshotId: option.Option<QuizSnapshotId>,
  ) => remoteOption.RemoteOption<Date>;

  useQuizSnapshotsSubscription: (taskId: TaskId) => remote.Remote<PublicationSet<QuizSnapshot>>;

  useQuestionTitlesSubscription: (
    taskId: TaskId,
    questionIds: Array<QuestionId>,
    snapshotDate: option.Option<Date>,
  ) => remote.Remote<Array<QuestionItem>>;

  useQuestionVersionId: (
    taskId: TaskId,
    questionId: QuestionId,
    snapshotDate: option.Option<Date>,
  ) => remoteOption.RemoteOption<QuestionVersionId>;

  useQuestionVersionSubscription: (
    taskId: TaskId,
    questionVersionId: QuestionVersionId,
  ) => remoteOption.RemoteOption<Question>;

  useLastModifiedSubscription: (taskId: TaskId, quizId: QuizId) => remote.Remote<Date>;

  updateQuiz: (taskId: TaskId, changes: Partial<QuizMutableProps>) => Promise<void>;

  createSnapshot: (snapshot: QuizSnapshotInsert) => Promise<QuizSnapshotId>;

  deleteSnapshot: (taskId: TaskId, snapshotId: QuizSnapshotId) => Promise<void>;

  renameSnapshot: (
    taskId: TaskId,
    snapshotId: QuizSnapshotId,
    name: option.Option<string>,
  ) => Promise<void>;

  restoreSnapshot: (taskId: TaskId, snapshotId: QuizSnapshotId) => Promise<void>;

  addQuestion: (taskId: TaskId) => Promise<QuestionId>;

  updateQuestion: (
    taskId: TaskId,
    questionId: QuestionId,
    changes: Partial<QuestionMutableProps>,
  ) => Promise<void>;

  deleteQuestion: (taskId: TaskId, questionId: QuestionId) => Promise<void>;

  reorderQuestion: (
    taskId: TaskId,
    questionId: QuestionId,
    moveBefore: QuestionId,
  ) => Promise<void>;
}

export const manageQuizApi: ManageQuizApi = {
  ...quizApi,

  useQuizVersionId: (taskId, snapshotDate) =>
    useCachedQuery('quizzes.getQuizVersionId', {
      props: { taskId, snapshotDate: option.toUndefined(snapshotDate) },
    })[0],

  useQuizVersionSubscription: (taskId, snapshotDate) => {
    const quizIdR = useCachedQuery('quizzes.getQuizVersionId', {
      props: {
        taskId,
        snapshotDate: option.toUndefined(snapshotDate),
      },
    })[0];

    return usePublication('quizzes.quizVersion', {
      propsO: pipe(
        quizIdR,
        remoteOption.map((quizId) => ({ taskId, quizId })),
        remoteOption.toOption,
      ),
      method: 'findOne',
    });
  },

  useQuizSnapshotSubscription: (taskId, snapshotId) => {
    const snapshot = usePublication('quizzes.getQuizSnapshot', {
      propsO: pipe(
        snapshotId,
        option.map((s) => ({ taskId, snapshotId: s })),
      ),
      method: 'findOne',
    });
    return useMemo(() => pipe(snapshot, remoteOption.map(struct.get('createdAt'))), [snapshot]);
  },

  useQuizSnapshotsSubscription: (taskId) =>
    usePublication('quizzes.getQuizSnapshots', { props: taskId }),

  useQuestionTitlesSubscription: (taskId, questionIds, snapshotDate) => {
    const questionTitles = usePublication('quizzes.questionTitles', {
      props: {
        taskId,
        questionIds,
        snapshotDate: option.toUndefined(snapshotDate),
      },
    });

    return useMemo(
      () =>
        pipe(
          questionTitles,
          remote.map((questions) => pipe(questionIds, array.filterMap(record.lookupV(questions)))),
        ),
      [questionTitles, questionIds],
    );
  },

  useQuestionVersionId: (taskId, questionId, snapshotDate) =>
    useCachedQuery('quizzes.getQuestionVersionId', {
      props: { taskId, questionId, snapshotDate: option.toUndefined(snapshotDate) },
    })[0],

  useQuestionVersionSubscription: (taskId, questionVersionId) =>
    usePublication('quizzes.questionVersion', {
      props: { taskId, questionVersionId },
      method: 'findOne',
    }),

  useLastModifiedSubscription: (taskId, quizId) =>
    pipe(
      usePublication('quizzes.lastModified', { props: { taskId, quizId }, method: 'getOne' }),
      remote.map(struct.get('lastModified')),
    ),

  updateQuiz: (taskId, changes) =>
    task.run(invokeProcedure('quizzes.updateQuiz')({ taskId, changes })),

  createSnapshot: (snapshot) => task.run(invokeProcedure('quizzes.createSnapshot')(snapshot)),

  renameSnapshot: (taskId, snapshotId, name) =>
    task.run(
      invokeProcedure('quizzes.renameSnapshot')({
        taskId,
        snapshotId,
        name: option.toUndefined(name),
      }),
    ),

  deleteSnapshot: (taskId, snapshotId) =>
    task.run(invokeProcedure('quizzes.deleteSnapshot')({ taskId, snapshotId })),

  restoreSnapshot: (taskId, snapshotId) =>
    task.run(invokeProcedure('quizzes.restoreSnapshot')({ taskId, snapshotId })),

  addQuestion: (taskId) => task.run(invokeProcedure('quizzes.addQuestion')(taskId)),

  deleteQuestion: (taskId, questionId) =>
    task.run(invokeProcedure('quizzes.deleteQuestion')({ taskId, questionId })),

  updateQuestion: (taskId, questionId, changes) =>
    task.run(invokeProcedure('quizzes.updateQuestion')({ taskId, questionId, changes })),

  reorderQuestion: (taskId, questionId, moveBefore) =>
    task.run(invokeProcedure('quizzes.reorderQuestion')({ taskId, questionId, moveBefore })),
};
